import { useCollectionView } from '../../app/use-collection-view.js';
import { SchedulingCredit } from './credit-slot.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  DatePicker,
  DateRangePicker,
  IconButton,
  Modal,
  Select,
  SegmentedControl,
  StatusPill,
  Textarea,
  TimePicker,
  formatCivilRange,
  formatCurrency,
  todayCivilDate,
  type CivilDate,
  useIsWideViewport,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  CalendarClock,
  CircleCheck,
  ClipboardCheck,
  Clock3,
  HeartPulse,
  MessageCircle,
  PencilLine,
  Plus,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { resourceKindLabel } from './resource-labels.js';
import { ResourcesPanel, type ResourceListView } from './resources-panel.js';
import {
  getApiError,
  schedulingApi,
  type Appointment,
  type AppointmentSort,
  type ScheduleBlock,
  type ScheduleBlockSort,
  type SchedulingResource,
} from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { SchedulingView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { AppointmentDialog, CancellationDialog } from './appointment-dialog.js';
import { roomLabel, professionalLabel } from './calendar-model.js';
import { CalendarPanel } from './company-calendar.js';
import { HolidaysPanel } from './holidays-panel.js';
import {
  optionList,
  useResourceOptions,
  useServiceOptions,
  type SchedulingCapabilities,
} from './scheduling-options.js';
import { AppointmentQueueNotice, AppointmentStatusPill, DialogActions } from './shared.js';
import { AppointmentDepositNotice } from './appointment-deposit.js';
import { AppointmentNoticesButton } from './appointment-notices.js';
import { SoldServiceBooking } from './sold-service-booking.js';
import { isLive, useAppointmentConfirmation } from './confirmation.js';
import {
  ChartLinkProvider,
  ClosingLinkProvider,
  useOpenChart,
  useOpenClosing,
} from './closing-link.js';
import { PageHeader } from '../../app/page-title.js';

export type { SchedulingCapabilities } from './scheduling-options.js';

const statusOptions = [
  { value: 'ALL', label: 'Todos' },
  { value: 'SCHEDULED', label: 'Agendados' },
  { value: 'CONFIRMED', label: 'Confirmados' },
  { value: 'COMPLETED', label: 'Concluídos' },
  { value: 'CANCELLED', label: 'Cancelados' },
  { value: 'NO_SHOW', label: 'Faltas' },
] as const;
const activityOptions = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
] as const;
const appointmentColumns: Array<{ sort: AppointmentSort; label: string }> = [
  { sort: 'startsAt', label: 'Horário' },
  { sort: 'clientDisplayName', label: 'Cliente' },
  { sort: 'serviceDisplayName', label: 'Serviço' },
];
const blockColumns: Array<{ sort: ScheduleBlockSort; label: string }> = [
  { sort: 'startsAt', label: 'Horário' },
  { sort: 'resourceDisplayName', label: 'Recurso' },
  { sort: 'createdAt', label: 'Criado em' },
];

export function CompanyScheduling({
  capabilities,
  timeZone,
  view,
  onView,
  onOpenClosing,
  onOpenChart,
  onOpenProfessionals,
}: {
  capabilities: SchedulingCapabilities;
  timeZone: string;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
  /// Atalho para o cadastro dos profissionais, que tem tela própria (ADR 0083).
  onOpenProfessionals?: (() => void) | undefined;
  /// Abre a página do atendimento, montada pela composição (ADR 0030).
  onOpenClosing?: ((appointmentId: string) => void) | undefined;
  /// Abre o Prontuário do cliente num painel lateral, montado pela composição (ADR 0038).
  onOpenChart?: ((clientId: string) => void) | undefined;
}) {
  const today = todayCivilDate(timeZone);
  const selectedDate = view.date ?? today;
  return (
    <ClosingLinkProvider value={onOpenClosing}>
      <ChartLinkProvider value={onOpenChart}>
        <section aria-labelledby="company-scheduling-title">
          <PageHeader
            trail="Operação / Agenda"
            id="company-scheduling-title"
            title="Agenda"
            description="Agendamentos, salas, bloqueios e feriados no horário da empresa."
            actions={
              <>
                {onOpenProfessionals ? (
                  <Button variant="outline" onClick={onOpenProfessionals}>
                    <UsersRound className="size-4" /> Profissionais
                  </Button>
                ) : null}
                <AppointmentNoticesButton canManage={capabilities.canUpdate} />
              </>
            }
          />
          {view.book && capabilities.canCreate ? (
            <SoldServiceBooking
              saleItemUnitId={view.book}
              today={today}
              timeZone={timeZone}
              onClose={() => onView({ book: '' })}
            />
          ) : null}
          <SegmentedControl
            tabs
            label="Áreas da agenda"
            className="mb-6"
            value={view.tab}
            onChange={(tab) => onView({ tab })}
            options={[
              { value: 'calendar', label: 'Calendário' },
              { value: 'appointments', label: 'Agendamentos' },
              { value: 'rooms', label: 'Salas' },
              { value: 'blocks', label: 'Bloqueios' },
              { value: 'holidays', label: 'Feriados' },
            ]}
          />
          {view.tab === 'appointments' ? (
            <AppointmentsPanel
              capabilities={capabilities}
              timeZone={timeZone}
              today={today}
              date={selectedDate}
              view={view}
              onView={onView}
            />
          ) : view.tab === 'calendar' ? (
            <CalendarPanel
              capabilities={capabilities}
              timeZone={timeZone}
              today={today}
              view={view}
              onView={onView}
            />
          ) : view.tab === 'rooms' ? (
            <div role="tabpanel">
              <ResourcesPanel
                kind="ROOM"
                canManage={capabilities.canManageResources}
                timeZone={timeZone}
                view={roomListView(view)}
                onView={(patch, options) => onView(roomViewPatch(patch), options)}
              />
            </div>
          ) : view.tab === 'holidays' ? (
            <HolidaysPanel
              canManage={capabilities.canManageHolidays ?? false}
              today={today}
              view={view}
              onView={onView}
            />
          ) : (
            <BlocksPanel
              capabilities={capabilities}
              timeZone={timeZone}
              today={today}
              date={selectedDate}
              view={view}
              onView={onView}
            />
          )}
        </section>
      </ChartLinkProvider>
    </ClosingLinkProvider>
  );
}

function roomListView(view: SchedulingView): ResourceListView {
  return {
    search: view.resourceSearch,
    activity: view.resourceActivity,
    sort: view.resourceSort,
    direction: view.resourceDirection,
    open: view.resource,
  };
}

function roomViewPatch(patch: Partial<ResourceListView>) {
  return {
    ...(patch.search === undefined ? {} : { resourceSearch: patch.search }),
    ...(patch.activity === undefined ? {} : { resourceActivity: patch.activity }),
    ...(patch.sort === undefined ? {} : { resourceSort: patch.sort }),
    ...(patch.direction === undefined ? {} : { resourceDirection: patch.direction }),
    ...(patch.open === undefined ? {} : { resource: patch.open }),
  };
}

function AppointmentsPanel({
  capabilities,
  timeZone,
  today,
  date,
  view,
  onView,
}: {
  capabilities: SchedulingCapabilities;
  timeZone: string;
  today: CivilDate;
  date: CivilDate;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const resources = useResourceOptions();
  const services = useServiceOptions();
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Appointment>();
  /// A agenda é de um dia só até alguém pedir mais: `to` ausente é o mesmo dia, que é como ela
  /// sempre abriu. Com os dois, é a semana — ou o intervalo que a pessoa apontou na grade.
  const until = view.to ?? date;
  const filters = {
    from: date,
    to: until,
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    status: view.status,
    ...(view.professional ? { professionalId: view.professional } : {}),
    ...(view.room ? { roomId: view.room } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'scheduling', 'appointments', filters],
    queryFn: () => schedulingApi.listAppointments(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'scheduling', 'appointment', view.appointment],
    queryFn: () => schedulingApi.getAppointment(view.appointment),
    enabled: Boolean(view.appointment),
    retry: false,
  });
  const professionals = (resources.data ?? []).filter(
    (item) => item.kind === 'PROFESSIONAL' && item.attendsSchedule !== false,
  );
  const rooms = (resources.data ?? []).filter((item) => item.kind === 'ROOM');
  const total = page.data?.total ?? 0;

  function toggleSort(column: AppointmentSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: 'asc' });
  }

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
  }

  /// No celular os campos de filtro ficam fechados dentro do diálogo: a etiqueta é o único sinal do
  /// que está filtrando a agenda. Recurso escolhido só vira etiqueta depois que a lista chega —
  /// sem o nome, a etiqueta seria um identificador na tela.
  const chips = [
    ...(view.status === 'ALL'
      ? []
      : [
          {
            key: 'status',
            label: statusOptions.find((item) => item.value === view.status)!.label,
            onRemove: () => onView({ status: 'ALL' }),
          },
        ]),
    ...professionals
      .filter((item) => item.id === view.professional)
      .map((item) => ({
        key: 'professional',
        label: item.displayName,
        onRemove: () => onView({ professional: '' }),
      })),
    ...rooms
      .filter((item) => item.id === view.room)
      .map((item) => ({
        key: 'room',
        label: item.displayName,
        onRemove: () => onView({ room: '' }),
      })),
    // Um dia só não vira etiqueta: a agenda sempre tem um dia, e etiquetar o estado de sempre seria
    // ruído. O intervalo, sim — no celular ele é o único sinal de que a lista passou de um dia.
    ...(view.to
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: date, to: until }, ''),
            onRemove: () => onView({ to: null }),
          },
        ]
      : []),
  ];

  return (
    <div role="tabpanel">
      <div className="mb-5 flex justify-end">
        {capabilities.canCreate ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Novo agendamento
          </Button>
        ) : null}
      </div>
      <FilterBar
        searchId="appointment-search"
        searchLabel="Buscar agendamento"
        searchPlaceholder="Cliente, serviço ou recurso"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ status: 'ALL', professional: '', room: '' })}
        count={`${total} ${total === 1 ? 'agendamento' : 'agendamentos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'agendamento' : 'agendamentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <DateRangePicker
          label="Dias da agenda"
          value={{ from: date, to: until }}
          onValueChange={(next) =>
            onView({
              date: next.from ?? today,
              // Um dia só não vira intervalo na URL: `to` igual ao começo é o estado de sempre.
              to: next.to && next.to !== next.from ? next.to : null,
            })
          }
          today={today}
          // "Voltar para hoje", e não "Hoje": o atalho do rodapé já se chama Hoje, e dois botões
          // com o mesmo nome no mesmo painel não dizem qual é qual.
          emptyLabel="Voltar para hoje"
        />
        <FilterGroup
          label="Situação"
          description="Filtrar agendamentos por situação"
          options={[...statusOptions]}
          value={view.status}
          onChange={(status) => onView({ status })}
        />
        <Select
          label="Profissional"
          value={view.professional}
          options={optionList(professionals, 'Todos os profissionais')}
          onValueChange={(professional) => onView({ professional })}
        />
        <Select
          label="Sala"
          value={view.room}
          options={optionList(rooms, 'Todas as salas')}
          onValueChange={(room) => onView({ room })}
        />
      </FilterBar>
      {page.isLoading || resources.isLoading ? (
        <LoadingCard label="Carregando agenda" />
      ) : page.error || resources.error ? (
        <ErrorCard message={getApiError(page.error ?? resources.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={CalendarClock}
          title="Nenhum agendamento encontrado"
          description="Ajuste o dia e os filtros ou crie o primeiro agendamento."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? null : (
            <div>
              <SortBar
                label="Ordenação dos agendamentos"
                columns={appointmentColumns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.appointments.map((appointment) => (
                  <li key={appointment.id} className="p-4">
                    <AppointmentSummary
                      appointment={appointment}
                      timeZone={timeZone}
                      capabilities={capabilities}
                      onEdit={() => onView({ appointment: appointment.id })}
                      onCancel={() => setCancelTarget(appointment)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Agendamentos da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {appointmentColumns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="w-52 px-5 py-3 text-xs">Recursos</th>
                  <th className="w-56">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.appointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="px-5 py-4 text-xs font-semibold tabular-nums">
                      {formatDateTime(appointment.startsAt, timeZone)}
                      <div className="mt-1">
                        <AppointmentStatusPill
                          status={appointment.status}
                          requiresConfirmation={appointment.requiresConfirmation}
                        />
                        <AppointmentQueueNotice appointment={appointment} timeZone={timeZone} />
                        <AppointmentDepositNotice appointment={appointment} timeZone={timeZone} />
                      </div>
                    </td>
                    <td className="truncate px-5 py-4 text-sm font-semibold">
                      {appointment.clientDisplayName}
                      <SchedulingCredit
                        clientId={appointment.clientId}
                        businessUnitId={appointment.businessUnitId}
                        compact
                      />
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      <strong className="block truncate text-content">
                        {appointment.serviceDisplayName}
                      </strong>
                      {appointment.durationMinutes} min ·{' '}
                      {formatCurrency(appointment.priceCents, appointment.currency)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      <span className="block truncate">{professionalLabel(appointment)}</span>
                      <span className="block truncate">{roomLabel(appointment)}</span>
                    </td>
                    <td className="px-3 py-4">
                      <AppointmentActions
                        appointment={appointment}
                        capabilities={capabilities}
                        onEdit={() => onView({ appointment: appointment.id })}
                        onCancel={() => setCancelTarget(appointment)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'agendamento' : 'agendamentos')}
          />
        </div>
      )}
      <AppointmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        appointment={null}
        today={today}
        timeZone={timeZone}
        services={services.data?.services ?? []}
        professionals={professionals}
        rooms={rooms}
        onSaved={async () => {
          await refresh();
          setCreateOpen(false);
          snackbar.success('Agendamento criado.');
        }}
      />
      <AppointmentDialog
        open={Boolean(view.appointment)}
        onOpenChange={() => onView({ appointment: '' })}
        appointment={detail.data ?? null}
        loading={detail.isLoading}
        error={detail.error ? getApiError(detail.error) : undefined}
        today={today}
        timeZone={timeZone}
        services={services.data?.services ?? []}
        professionals={professionals}
        rooms={rooms}
        readOnly={!capabilities.canUpdate}
        onSaved={async () => {
          await refresh();
          onView({ appointment: '' });
          snackbar.success('Agendamento remarcado.');
        }}
      />
      {cancelTarget ? (
        <CancellationDialog
          appointment={cancelTarget}
          onClose={() => setCancelTarget(undefined)}
          onSaved={async () => {
            await refresh();
            setCancelTarget(undefined);
            snackbar.success('Agendamento cancelado.');
          }}
        />
      ) : null}
    </div>
  );
}

function AppointmentSummary({
  appointment,
  timeZone,
  capabilities,
  onEdit,
  onCancel,
}: {
  appointment: Appointment;
  timeZone: string;
  capabilities: SchedulingCapabilities;
  onEdit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
        <Clock3 className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <strong className="block truncate text-sm">{appointment.clientDisplayName}</strong>
        <SchedulingCredit
          clientId={appointment.clientId}
          businessUnitId={appointment.businessUnitId}
          compact
        />
        <span className="mt-1 block text-xs font-semibold tabular-nums">
          {formatDateTime(appointment.startsAt, timeZone)}
        </span>
        <span className="mt-1 block truncate text-xs text-content-muted">
          {appointment.serviceDisplayName} · {professionalLabel(appointment)}
        </span>
        <div className="mt-2">
          <AppointmentStatusPill
            status={appointment.status}
            requiresConfirmation={appointment.requiresConfirmation}
          />
          <AppointmentQueueNotice appointment={appointment} timeZone={timeZone} />
        </div>
      </div>
      <AppointmentActions
        appointment={appointment}
        capabilities={capabilities}
        onEdit={onEdit}
        onCancel={onCancel}
      />
    </div>
  );
}

function AppointmentActions({
  appointment,
  capabilities,
  onEdit,
  onCancel,
}: {
  appointment: Appointment;
  capabilities: SchedulingCapabilities;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const confirmation = useAppointmentConfirmation();
  const openClosing = useOpenClosing();
  const openChart = useOpenChart();
  /// Os cinco ícones ocupam sempre o mesmo lugar em toda linha: o que não vale para o
  /// agendamento, para o módulo contratado ou para a permissão da pessoa aparece apagado, em vez de
  /// sumir e desalinhar a coluna.
  const live = isLive(appointment.status);
  const attended = appointment.status === 'COMPLETED' || appointment.status === 'NO_SHOW';
  const busy = confirmation.pending === appointment.id;
  const canConfirm = live && capabilities.canUpdate && !busy;
  return (
    <div className="flex flex-wrap justify-end gap-1 lg:flex-nowrap">
      <IconButton
        label="Prontuário"
        icon={<HeartPulse className="size-4" />}
        disabled={!openChart || !(live || attended)}
        onClick={() => openChart?.(appointment.clientId)}
      />
      <IconButton
        label={appointment.status === 'COMPLETED' ? 'Ver atendimento' : 'Abrir atendimento'}
        icon={<ClipboardCheck className="size-4" />}
        disabled={!openClosing || !(live || attended)}
        onClick={() => openClosing?.(appointment.id)}
      />
      {appointment.status === 'CONFIRMED' ? (
        <IconButton
          label="Reenviar confirmação"
          icon={<MessageCircle className="size-4" />}
          disabled={!canConfirm}
          onClick={() => void confirmation.confirm(appointment)}
        />
      ) : (
        <IconButton
          label="Confirmar agendamento"
          icon={<CircleCheck className="size-4" />}
          disabled={!canConfirm}
          onClick={() => void confirmation.confirm(appointment)}
        />
      )}
      <IconButton
        label="Remarcar"
        icon={<PencilLine className="size-4" />}
        disabled={!live || !capabilities.canUpdate}
        onClick={onEdit}
      />
      <IconButton
        label="Cancelar agendamento"
        tone="danger"
        icon={<Ban className="size-4" />}
        disabled={!live || !capabilities.canCancel}
        onClick={onCancel}
      />
    </div>
  );
}

function BlocksPanel({
  capabilities,
  timeZone,
  today,
  date,
  view,
  onView,
}: {
  capabilities: SchedulingCapabilities;
  timeZone: string;
  today: CivilDate;
  date: CivilDate;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const resources = useResourceOptions();
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ScheduleBlock>();
  const until = view.to ?? date;
  const filters = {
    from: date,
    to: until,
    ...(view.blockSearch.trim() ? { search: view.blockSearch.trim() } : {}),
    activity: view.blockActivity,
    ...(view.blockResource ? { resourceId: view.blockResource } : {}),
    sort: view.blockSort,
    direction: view.blockDirection,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'scheduling', 'blocks', filters],
    queryFn: () => schedulingApi.listBlocks(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  function toggleSort(column: ScheduleBlockSort) {
    if (view.blockSort === column)
      onView({ blockDirection: view.blockDirection === 'asc' ? 'desc' : 'asc' });
    else onView({ blockSort: column, blockDirection: 'asc' });
  }
  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
  }
  async function cancel(block: ScheduleBlock) {
    try {
      await schedulingApi.cancelBlock(block.id);
      await refresh();
      setCancelTarget(undefined);
      snackbar.success('Bloqueio cancelado.');
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    }
  }
  const chips = [
    ...(view.blockActivity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.blockActivity)!.label,
            onRemove: () => onView({ blockActivity: 'ALL' }),
          },
        ]),
    ...(resources.data ?? [])
      .filter((item) => item.id === view.blockResource)
      .map((item) => ({
        key: 'resource',
        label: item.displayName,
        onRemove: () => onView({ blockResource: '' }),
      })),
    ...(view.to
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: date, to: until }, ''),
            onRemove: () => onView({ to: null }),
          },
        ]
      : []),
  ];
  return (
    <div role="tabpanel">
      <div className="mb-5 flex justify-end">
        {capabilities.canManageBlocks ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Bloquear horário
          </Button>
        ) : null}
      </div>
      <FilterBar
        searchId="block-search"
        searchLabel="Buscar bloqueio"
        searchPlaceholder="Motivo ou recurso"
        search={view.blockSearch}
        onSearchChange={(blockSearch) => onView({ blockSearch }, { replace: true })}
        chips={chips}
        onClear={() => onView({ blockActivity: 'ALL', blockResource: '' })}
        count={`${total} ${total === 1 ? 'bloqueio' : 'bloqueios'}`}
        resultsLabel={`Ver ${total} bloqueios`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <DateRangePicker
          label="Dias dos bloqueios"
          value={{ from: date, to: until }}
          onValueChange={(next) =>
            onView({
              date: next.from ?? today,
              to: next.to && next.to !== next.from ? next.to : null,
            })
          }
          today={today}
          emptyLabel="Voltar para hoje"
        />
        <FilterGroup
          label="Situação"
          description="Filtrar bloqueios por situação"
          options={[...activityOptions]}
          value={view.blockActivity}
          onChange={(blockActivity) => onView({ blockActivity })}
        />
        <Select
          label="Recurso"
          value={view.blockResource}
          options={optionList(resources.data ?? [], 'Todos os recursos')}
          onValueChange={(blockResource) => onView({ blockResource })}
        />
      </FilterBar>
      {page.isLoading || resources.isLoading ? (
        <LoadingCard label="Carregando bloqueios" />
      ) : page.error || resources.error ? (
        <ErrorCard message={getApiError(page.error ?? resources.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Ban}
          title="Nenhum bloqueio encontrado"
          description="Bloqueie férias, manutenção e outras indisponibilidades excepcionais."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? null : (
            <div>
              <SortBar
                label="Ordenação dos bloqueios"
                columns={blockColumns}
                sort={view.blockSort}
                direction={view.blockDirection}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.blocks.map((block) => (
                  <li key={block.id} className="flex items-start gap-3 p-4">
                    <Ban className="mt-1 size-5 shrink-0 text-brand-400" />
                    <div className="min-w-0 flex-1">
                      <strong className="block truncate text-sm">
                        {block.resourceDisplayName}
                      </strong>
                      <span className="block text-xs text-content-muted">
                        {formatDateTime(block.startsAt, timeZone)} · {block.reason}
                      </span>
                      {block.active ? null : <StatusPill tone="neutral">Cancelado</StatusPill>}
                    </div>
                    {block.active && capabilities.canManageBlocks ? (
                      <IconButton
                        label="Cancelar bloqueio"
                        tone="danger"
                        icon={<Ban className="size-4" />}
                        onClick={() => setCancelTarget(block)}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Bloqueios da agenda</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {blockColumns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.blockSort}
                      direction={view.blockDirection}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="w-72 px-5 py-3 text-xs">Motivo</th>
                  <th className="w-20">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.blocks.map((block) => (
                  <tr key={block.id}>
                    <td className="px-5 py-4 text-xs font-semibold">
                      {formatDateTime(block.startsAt, timeZone)} –{' '}
                      {formatDateTime(block.endsAt, timeZone)}
                    </td>
                    <td className="truncate px-5 py-4 text-sm font-semibold">
                      {block.resourceDisplayName}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDateTime(block.createdAt, timeZone)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {block.reason}
                      {block.active ? null : (
                        <div className="mt-1">
                          <StatusPill tone="neutral">Cancelado</StatusPill>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {block.active && capabilities.canManageBlocks ? (
                        <IconButton
                          label="Cancelar bloqueio"
                          tone="danger"
                          icon={<Ban className="size-4" />}
                          onClick={() => setCancelTarget(block)}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'bloqueio' : 'bloqueios')}
          />
        </div>
      )}
      <BlockDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        date={date}
        today={today}
        resources={resources.data ?? []}
        onSaved={async () => {
          await refresh();
          setCreateOpen(false);
          snackbar.success('Horário bloqueado.');
        }}
      />
      {cancelTarget ? (
        <Modal
          open
          onOpenChange={() => setCancelTarget(undefined)}
          title="Cancelar bloqueio"
          description="O intervalo voltará a ficar disponível para agendamento."
        >
          <p className="text-sm">
            Cancelar o bloqueio de <strong>{cancelTarget.resourceDisplayName}</strong>?
          </p>
          <DialogActions
            label="Cancelar bloqueio"
            onCancel={() => setCancelTarget(undefined)}
            onSubmit={() => void cancel(cancelTarget)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

const blockFormSchema = z
  .object({
    resourceId: z.string().min(1, 'Escolha o recurso.'),
    date: z.string().min(1),
    startTime: z.string().min(1, 'Informe o início.'),
    endTime: z.string().min(1, 'Informe o fim.'),
    reason: z.string().trim().min(3, 'Informe o motivo.').max(500),
  })
  .refine((value) => value.startTime < value.endTime, {
    path: ['endTime'],
    message: 'O fim deve ser posterior ao início.',
  });
type BlockForm = z.infer<typeof blockFormSchema>;
function BlockDialog({
  open,
  onOpenChange,
  date,
  today,
  resources,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: CivilDate;
  today: CivilDate;
  resources: SchedulingResource[];
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<BlockForm>({
    resolver: zodResolver(blockFormSchema),
    defaultValues: { resourceId: '', date, startTime: '09:00', endTime: '10:00', reason: '' },
  });
  const values = useWatch({ control: form.control }) as BlockForm;
  useEffect(() => {
    if (open) {
      form.reset({ resourceId: '', date, startTime: '09:00', endTime: '10:00', reason: '' });
    }
  }, [date, form, open]);
  function changeOpen(nextOpen: boolean) {
    if (!nextOpen) setRequestError(undefined);
    onOpenChange(nextOpen);
  }
  async function save(data: BlockForm) {
    setRequestError(undefined);
    try {
      await schedulingApi.createBlock(data);
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title="Bloquear horário"
      description="O bloqueio ocupa o recurso como um agendamento e pode ser cancelado depois."
    >
      <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Combobox
            className="sm:col-span-2"
            label="Recurso"
            placeholder="Buscar recurso"
            selectedLabel={
              resources.find((resource) => resource.id === values.resourceId)?.displayName ?? ''
            }
            options={resources.map((resource) => ({
              value: resource.id,
              label: resource.displayName,
              description: resourceKindLabel(resource),
            }))}
            // Sem texto livre, toda escolha é uma opção da lista.
            onSelect={(choice) =>
              form.setValue('resourceId', (choice as { value: string }).value, {
                shouldValidate: true,
              })
            }
            onClear={() => form.setValue('resourceId', '')}
            emptyLabel="Nenhum recurso encontrado"
            error={form.formState.errors.resourceId?.message}
          />
          <DatePicker
            label="Data"
            value={values.date}
            onValueChange={(value) => form.setValue('date', value ?? '', { shouldValidate: true })}
            today={today}
          />
          <span />
          <TimePicker
            label="Início"
            value={values.startTime || null}
            onValueChange={(value) =>
              form.setValue('startTime', value ?? '', { shouldValidate: true })
            }
            error={form.formState.errors.startTime?.message}
          />
          <TimePicker
            label="Fim"
            value={values.endTime || null}
            onValueChange={(value) =>
              form.setValue('endTime', value ?? '', { shouldValidate: true })
            }
            error={form.formState.errors.endTime?.message}
          />
          <div className="sm:col-span-2">
            <Textarea
              label="Motivo"
              rows={4}
              error={form.formState.errors.reason?.message}
              {...form.register('reason')}
            />
          </div>
        </div>
        {requestError ? <RequestAlert message={requestError} /> : null}
        <DialogActions
          label="Bloquear horário"
          onCancel={() => changeOpen(false)}
          loading={form.formState.isSubmitting}
        />
      </form>
    </Modal>
  );
}
